import type { Environment, SectionDetail, Variable } from '@fleequid-secrets/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, messageOf } from '@/api/client';
import { copyText, downloadText } from '@/lib/clipboard';
import { ago, plural } from '@/lib/format';
import { typing, useNow, useOutside } from '@/lib/hooks';
import { overlayOpen } from '@/lib/overlay';
import { useRevealed } from '@/lib/revealed';
import { Link, navigate, setParam } from '@/lib/router';
import { comparePath, vaultPath } from '@/lib/routes';
import { Cancelled, goPending, guarded, usePageCommands, useShell } from '@/lib/shell';
import { HistorySheet } from './HistorySheet';
import { Icon } from './Icon';
import { ImportDialog } from './ImportDialog';
import { CopySection, DeleteSection, RenameSection } from './SectionDialogs';
import { type VariableDraft, VariableEditor } from './VariableEditor';
import { VariableRow } from './VariableRow';

type Dialog = 'rename' | 'copy' | 'delete' | null;

// The section on screen: its variables, masked, and everything done to them. Keyed by the section, so moving to
// another one starts clean (nothing revealed carries over).
export function SectionBoard(props: { environment: Environment; sectionId: string; highlight: string | null }): ReactNode {
    const shell = useShell();
    const queryClient = useQueryClient();
    const now = useNow(30_000);
    const detail = useQuery({ queryKey: ['section', props.sectionId], queryFn: ({ signal }) => api.section(props.sectionId, signal) });
    const revealed = useRevealed();
    const [filter, setFilter] = useState('');
    const [cursor, setCursor] = useState<string | null>(null);
    const [flash, setFlash] = useState<string | null>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<{ id: string; value: string } | null>(null);
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [armed, setArmed] = useState<string | null>(null);
    const [history, setHistory] = useState<string | null>(null);
    const [importing, setImporting] = useState<string | null>(null);
    const [dialog, setDialog] = useState<Dialog>(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const exportMenu = useOutside(exportOpen, () => setExportOpen(false));
    const moreMenu = useOutside(moreOpen, () => setMoreOpen(false));
    const filterField = useRef<HTMLInputElement>(null);

    const data: SectionDetail | undefined = detail.data;
    const environment = props.environment;
    const section = data?.section ?? environment.sections.find((item) => item.id === props.sectionId);
    const variables = useMemo(() => data?.variables ?? [], [data]);
    const words = filter.trim().toLowerCase();
    const visible = useMemo(
        () =>
            words
                ? variables.filter((variable) => variable.key.toLowerCase().includes(words) || variable.note.toLowerCase().includes(words))
                : variables,
        [variables, words],
    );
    const elsewhere = environment.sections.length
        ? shell.environments.filter((other) => other.id !== environment.id && other.sections.some((item) => item.slug === section?.slug)).length
        : 0;
    const latest = variables.reduce<Variable | null>((last, variable) => (!last || variable.updatedAt > last.updatedAt ? variable : last), null);
    const allShown = variables.length > 0 && variables.every((variable) => revealed.get(variable.id) !== undefined);

    const refresh = useCallback(async (): Promise<void> => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['section', props.sectionId] }),
            queryClient.invalidateQueries({ queryKey: ['environments'] }),
            queryClient.invalidateQueries({ queryKey: ['keys'] }),
            queryClient.invalidateQueries({ queryKey: ['activity'] }),
        ]);
    }, [queryClient, props.sectionId]);
    const fail = useCallback((error: unknown) => !(error instanceof Cancelled) && shell.say(messageOf(error)), [shell]);

    // A key asked for in the address (from ⌘K, COMPARE, ACTIVITY): the cursor on it, lit for a moment, scrolled to;
    // then the address forgets it. Taken while rendering, once the section's variables are there.
    const [taken, setTaken] = useState<string | null>(null);
    const asked = props.highlight && data ? data.variables.find((variable) => variable.key === props.highlight) : undefined;
    if (props.highlight && data && taken !== props.highlight) {
        setTaken(props.highlight);
        if (asked) {
            setCursor(asked.id);
            setFlash(asked.id);
        }
    }
    useEffect(() => {
        if (!props.highlight || !data) {
            return;
        }
        if (asked) {
            requestAnimationFrame(() => document.querySelector(`[data-id="${asked.id}"]`)?.scrollIntoView({ block: 'center' }));
        }
        setParam('key', null);
    }, [props.highlight, data, asked]);
    useEffect(() => {
        if (!armed) {
            return;
        }
        const timer = setTimeout(() => setArmed(null), 4000);

        return () => clearTimeout(timer);
    }, [armed]);

    const toggle = useCallback(
        (variable: Variable): void => {
            if (revealed.get(variable.id) !== undefined) {
                revealed.hide(variable.id);

                return;
            }
            api.reveal(props.sectionId, [variable.id], 'reveal')
                .then(({ values }) => revealed.show(values))
                .catch(fail);
        },
        [revealed, props.sectionId, fail],
    );
    const copy = useCallback(
        (variable: Variable): void => {
            api.reveal(props.sectionId, [variable.id], 'copy')
                .then(({ values }) => copyText(values[variable.id] ?? ''))
                .then(() => shell.say(`Copied ${variable.key}`))
                .catch(fail);
        },
        [props.sectionId, shell, fail],
    );
    const revealAll = (): void => {
        if (allShown) {
            revealed.clear();

            return;
        }
        api.reveal(props.sectionId, null, 'reveal')
            .then(({ values }) => revealed.show(values))
            .catch(fail);
    };
    const edit = useCallback(
        (variable: Variable): void => {
            setEditing(variable.id);
            setEditError(null);
            setArmed(null);
            const shown = revealed.get(variable.id);
            if (shown !== undefined) {
                setEditValue({ id: variable.id, value: shown });

                return;
            }
            setEditValue(null);
            api.reveal(props.sectionId, [variable.id], 'reveal')
                .then(({ values }) => setEditValue({ id: variable.id, value: values[variable.id] ?? '' }))
                .catch((error: unknown) => {
                    setEditing(null);
                    fail(error);
                });
        },
        [revealed, props.sectionId, fail],
    );
    const remove = useCallback(
        (variable: Variable): void => {
            if (armed !== variable.id) {
                setArmed(variable.id);

                return;
            }
            setArmed(null);
            guarded(shell, environment, `delete ${variable.key}`, (confirm) => api.deleteVariable(variable.id, confirm))
                .then(async () => {
                    revealed.hide(variable.id);
                    await refresh();
                    shell.say(`Deleted ${variable.key}`, {
                        label: 'Undo',
                        run: () => {
                            guarded(shell, environment, `bring back ${variable.key}`, (confirm) => api.restoreVariable(variable.id, confirm))
                                .then(refresh)
                                .then(() => shell.say(`${variable.key} is back`))
                                .catch(fail);
                        },
                    });
                })
                .catch(fail);
        },
        [armed, shell, environment, revealed, refresh, fail],
    );
    const save = (draft: VariableDraft): void => {
        if (!section) {
            return;
        }
        const current = editing && editing !== 'new' ? variables.find((variable) => variable.id === editing) : undefined;
        setSaving(true);
        setEditError(null);
        const run = current
            ? guarded(shell, environment, `change ${current.key}`, (confirm) =>
                  api.updateVariable(current.id, { key: draft.key, value: draft.value, note: draft.note, baseVersion: current.version }, confirm),
              )
            : guarded(shell, environment, `add ${draft.key}`, (confirm) => api.createVariable(section.id, draft, confirm));
        run.then(async ({ variable }) => {
            revealed.hide(variable.id);
            setEditing(null);
            setEditValue(null);
            setCursor(variable.id);
            setFlash(variable.id);
            await refresh();
            shell.say(
                current
                    ? variable.version > current.version
                        ? `Saved ${variable.key}, version ${variable.version}`
                        : `Saved ${variable.key}`
                    : `Added ${variable.key}`,
            );
        })
            .catch((error: unknown) => {
                if (error instanceof Cancelled) {
                    return;
                }
                setEditError(error instanceof ApiError && error.code === 'stale' ? `${error.message}.` : messageOf(error));
                if (error instanceof ApiError && error.code === 'stale') {
                    void refresh();
                }
            })
            .finally(() => setSaving(false));
    };
    const exportAs = (how: 'download' | 'copy'): void => {
        setExportOpen(false);
        api.exportSection(props.sectionId)
            .then(async ({ filename, content, count }) => {
                if (how === 'download') {
                    downloadText(filename, content);
                    shell.say(`${filename} downloaded: ${plural(count, 'variable')}`);
                } else {
                    await copyText(content);
                    shell.say(`${plural(count, 'variable')} copied as a .env`);
                }
                await queryClient.invalidateQueries({ queryKey: ['activity'] });
            })
            .catch(fail);
    };
    const neighbour = (step: number): void => {
        const list = environment.sections;
        const index = list.findIndex((item) => item.id === props.sectionId);
        const next = list[(index + step + list.length) % list.length];
        if (next && next.id !== props.sectionId) {
            navigate(vaultPath(environment.slug, next.slug));
        }
    };

    // The keys of a section, while no field has them and nothing modal is open.
    const keys = useRef<(event: KeyboardEvent) => void>(() => undefined);
    const onKeys = (event: KeyboardEvent): void => {
        if (overlayOpen() || goPending() || event.metaKey || event.ctrlKey || event.altKey) {
            return;
        }
        if (typing(event)) {
            if (event.key === 'Escape' && event.target === filterField.current) {
                setFilter('');
                filterField.current?.blur();
            }

            return;
        }
        const index = visible.findIndex((variable) => variable.id === cursor);
        const at = visible[index];
        const move = (step: number): void => {
            const next = visible[Math.min(visible.length - 1, Math.max(0, index < 0 ? 0 : index + step))];
            if (next) {
                setCursor(next.id);
                document.querySelector(`[data-id="${next.id}"]`)?.scrollIntoView({ block: 'nearest' });
            }
        };
        // A shifted letter reads as its capital, whichever way the keyboard reports it.
        const key = event.shiftKey && event.key.length === 1 ? event.key.toUpperCase() : event.key;
        const handled = ((): boolean => {
            switch (key) {
                case '/':
                    filterField.current?.focus();

                    return true;
                case 'n':
                    setEditing('new');
                    setEditError(null);

                    return true;
                case 'i':
                    setImporting('');

                    return true;
                case 'ArrowDown':
                case 'j':
                    move(1);

                    return true;
                case 'ArrowUp':
                case 'k':
                    move(-1);

                    return true;
                case '[':
                    neighbour(-1);

                    return true;
                case ']':
                    neighbour(1);

                    return true;
                case 'R':
                    revealAll();

                    return true;
                case 'Escape':
                    if (history) {
                        setHistory(null);
                    } else {
                        setCursor(null);
                    }

                    return true;
            }
            if (!at) {
                return false;
            }
            switch (key) {
                case 'Enter':
                    edit(at);

                    return true;
                case ' ':
                    toggle(at);

                    return true;
                case 'c':
                    copy(at);

                    return true;
                case 'h':
                    setHistory(at.id);

                    return true;
            }

            return false;
        })();
        if (handled) {
            event.preventDefault();
        }
    };
    useEffect(() => {
        keys.current = onKeys;
    });
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => keys.current(event);
        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, []);

    usePageCommands(
        section
            ? [
                  { icon: 'plus', label: `New variable in ${section.name}`, key: 'N', run: () => setEditing('new') },
                  { icon: 'upload', label: `Import a .env into ${section.name}`, key: 'I', run: () => setImporting('') },
                  { icon: 'download', label: `Download ${section.name} as a .env`, run: () => exportAs('download') },
                  {
                      icon: allShown ? 'eyeoff' : 'eye',
                      label: allShown ? 'Hide every value' : `Reveal every value of ${section.name}`,
                      key: '⇧R',
                      run: revealAll,
                  },
                  { icon: 'columns', label: `Compare ${section.name} across environments`, run: () => navigate(comparePath(section.slug)) },
              ]
            : [],
    );

    if (detail.isError) {
        return (
            <section className="board panel">
                <div className="empty">
                    <b>This section did not open</b>
                    {messageOf(detail.error)}
                </div>
            </section>
        );
    }
    if (!section) {
        return <section className="board panel" />;
    }
    const historyOf = history ? variables.find((variable) => variable.id === history) : undefined;
    const rowActions = {
        onCursor: setCursor,
        onToggle: toggle,
        onCopy: copy,
        onEdit: edit,
        onHistory: (variable: Variable) => setHistory(variable.id),
        onDelete: remove,
    };

    return (
        <section className="board panel">
            <header className="board-head">
                <div className="board-title">
                    <div className="crumb">
                        <i className="dot halo" style={{ '--c': environment.color } as CSSProperties} />
                        {environment.name}
                        {environment.protected && <Icon name="lock" size={13} />}
                    </div>
                    <h1>{section.name}</h1>
                    <p>
                        {section.description && <>{section.description} · </>}
                        <b>{plural(variables.length, 'variable')}</b>
                        {latest && (
                            <>
                                {' '}
                                · changed {ago(latest.updatedAt, now)}
                                {latest.updatedBy ? ` by ${latest.updatedBy.name}` : ''}
                            </>
                        )}
                        {elsewhere > 0 && (
                            <>
                                {' '}
                                ·{' '}
                                <Link className="link" to={comparePath(section.slug)}>
                                    in {plural(elsewhere + 1, 'environment')}
                                </Link>
                            </>
                        )}
                    </p>
                </div>
                <div className="board-actions">
                    <button
                        className={`ghost ${allShown ? 'on' : ''}`}
                        onClick={revealAll}
                        disabled={variables.length === 0}
                        title="Reveal or hide every value (⇧R)"
                    >
                        <Icon name={allShown ? 'eyeoff' : 'eye'} size={16} />
                        <span className="label">{allShown ? 'Hide values' : 'Reveal values'}</span>
                    </button>
                    <button className="act" onClick={() => setImporting('')} title="Import a .env (I)">
                        <Icon name="upload" size={15} />
                        <span className="label">Import</span>
                    </button>
                    <div className="envpick" ref={exportMenu}>
                        <button
                            className={`act ${exportOpen ? 'on' : ''}`}
                            onClick={() => setExportOpen(!exportOpen)}
                            disabled={variables.length === 0}
                        >
                            <Icon name="download" size={15} />
                            <span className="label">Export</span>
                            <Icon name="chevron" size={14} />
                        </button>
                        {exportOpen && (
                            <div className="menu right float">
                                <button onClick={() => exportAs('download')}>
                                    <Icon name="download" size={16} /> Download {environment.slug}-{section.slug}.env
                                </button>
                                <button onClick={() => exportAs('copy')}>
                                    <Icon name="copy" size={16} /> Copy as a .env
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="envpick" ref={moreMenu}>
                        <button className={`act ${moreOpen ? 'on' : ''}`} onClick={() => setMoreOpen(!moreOpen)} title="More">
                            <Icon name="dots" size={16} />
                        </button>
                        {moreOpen && (
                            <div className="menu right float">
                                <button onClick={() => (setMoreOpen(false), navigate(comparePath(section.slug)))}>
                                    <Icon name="columns" size={16} /> Compare across environments
                                </button>
                                <button onClick={() => (setMoreOpen(false), setDialog('copy'))} disabled={shell.environments.length < 2}>
                                    <Icon name="swap" size={16} /> Copy to another environment
                                </button>
                                <button onClick={() => (setMoreOpen(false), setDialog('rename'))}>
                                    <Icon name="pencil" size={16} /> Rename the section
                                </button>
                                <div className="sep" />
                                <button className="danger" onClick={() => (setMoreOpen(false), setDialog('delete'))}>
                                    <Icon name="trash" size={16} /> Delete the section
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        className={`act ${variables.length > 0 ? 'primary' : ''}`}
                        onClick={() => (setEditing('new'), setEditError(null))}
                        title="New variable (N)"
                    >
                        <Icon name="plus" size={15} />
                        <span className="label">Add variable</span>
                    </button>
                </div>
            </header>
            {variables.length > 0 && (
                <div className="board-tools">
                    <div className="filterbox">
                        <Icon name="search" size={15} />
                        <input
                            ref={filterField}
                            className="field"
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                            placeholder="Filter the keys"
                            spellCheck={false}
                        />
                        {!filter && <span className="kbd">/</span>}
                    </div>
                    {words && <span className="found">{plural(visible.length, 'match', 'matches')}</span>}
                </div>
            )}
            <div className="vars">
                {editing === 'new' && (
                    <VariableEditor
                        mode="new"
                        initial={{ key: '', value: '', note: '' }}
                        loading={false}
                        busy={saving}
                        error={editError}
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onPasteFile={(text) => {
                            setEditing(null);
                            setImporting(text);
                        }}
                    />
                )}
                {variables.length > 0 && (
                    <div className="vhead">
                        <span>Key</span>
                        <span>Value</span>
                        <span className="vnote">Note</span>
                        <span>Changed</span>
                        <span />
                    </div>
                )}
                {visible.map((variable) =>
                    editing === variable.id ? (
                        <VariableEditor
                            key={`${variable.id}:${editValue?.id === variable.id ? 'open' : 'opening'}`}
                            mode="edit"
                            initial={{ key: variable.key, value: editValue?.id === variable.id ? editValue.value : '', note: variable.note }}
                            loading={editValue?.id !== variable.id}
                            busy={saving}
                            error={editError}
                            onSave={save}
                            onCancel={() => (setEditing(null), setEditValue(null))}
                            onPasteFile={(text) => setImporting(text)}
                        />
                    ) : (
                        <VariableRow
                            key={variable.id}
                            variable={variable}
                            value={revealed.get(variable.id)}
                            until={revealed.until(variable.id)}
                            cursor={cursor === variable.id}
                            flash={flash === variable.id}
                            armed={armed === variable.id}
                            now={now}
                            {...rowActions}
                        />
                    ),
                )}
                {variables.length > 0 && visible.length === 0 && (
                    <div className="empty">
                        <b>No key matches “{filter.trim()}”</b>
                        Keys and notes are searched; values never are.
                    </div>
                )}
                {detail.isSuccess && variables.length === 0 && editing !== 'new' && (
                    <EmptySection
                        onImport={setImporting}
                        onAdd={() => setEditing('new')}
                        sectionName={section.name}
                        environmentName={environment.name}
                    />
                )}
            </div>
            {historyOf && (
                <HistorySheet
                    key={historyOf.id}
                    environment={environment}
                    section={section}
                    variable={historyOf}
                    onClose={() => setHistory(null)}
                    onRestored={() => {
                        void refresh();
                        revealed.hide(historyOf.id);
                    }}
                />
            )}
            {importing !== null && (
                <ImportDialog
                    environment={environment}
                    section={section}
                    initial={importing}
                    onClose={() => setImporting(null)}
                    onImported={() => {
                        setImporting(null);
                        revealed.clear();
                        void refresh();
                    }}
                />
            )}
            {dialog === 'rename' && (
                <RenameSection
                    environment={environment}
                    section={section}
                    onClose={() => setDialog(null)}
                    onDone={(renamed) => {
                        setDialog(null);
                        void refresh().then(() => navigate(vaultPath(environment.slug, renamed.slug), { replace: true }));
                    }}
                />
            )}
            {dialog === 'copy' && (
                <CopySection
                    environment={environment}
                    section={section}
                    environments={shell.environments}
                    onClose={() => {
                        setDialog(null);
                        void refresh();
                    }}
                    onDone={(target, slug) => navigate(vaultPath(target.slug, slug))}
                />
            )}
            {dialog === 'delete' && (
                <DeleteSection
                    environment={environment}
                    section={section}
                    onClose={() => setDialog(null)}
                    onDone={() => {
                        setDialog(null);
                        shell.say(`${section.name} deleted from ${environment.name}`);
                        void queryClient
                            .invalidateQueries({ queryKey: ['environments'] })
                            .then(() => navigate(vaultPath(environment.slug), { replace: true }));
                    }}
                />
            )}
        </section>
    );
}

// An empty section: the way in is pasting the whole .env (a Bitwarden note, a file), or one variable at a time.
function EmptySection(props: { sectionName: string; environmentName: string; onImport: (text: string) => void; onAdd: () => void }): ReactNode {
    const [over, setOver] = useState(false);

    return (
        <div
            className={`dropzone ${over ? 'over' : ''}`}
            onDragOver={(event) => {
                event.preventDefault();
                setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(event) => {
                event.preventDefault();
                setOver(false);
                const file = event.dataTransfer.files[0];
                if (file) {
                    void file.text().then(props.onImport);
                }
            }}
            onPaste={(event) => {
                const text = event.clipboardData.getData('text');
                if (text.trim()) {
                    props.onImport(text);
                }
            }}
            tabIndex={0}
        >
            <Icon name="upload" size={22} />
            <b>
                {props.environmentName} · {props.sectionName} is empty
            </b>
            <span>Paste a .env here (⌘V), or drop the file: you see what it holds before anything is saved.</span>
            <div className="row">
                <button className="act primary" onClick={() => props.onImport('')}>
                    <Icon name="upload" size={15} /> Import a .env
                </button>
                <button className="act" onClick={props.onAdd}>
                    <Icon name="plus" size={15} /> Add a variable
                </button>
            </div>
        </div>
    );
}
